/**
 * The mark a material's row wears, chosen from the host the server resolved.
 *
 * Three brands and a neutral glyph, because that is what the 17 September 2026 canon draws on a
 * link row: YouTube, Google Drive, Instagram, and a chain link for everything else. The KEY is
 * `entities/exercise-library`'s `brandKeyOf`, which reads the host; this file only draws.
 *
 * Its consumer is this slice's `MaterialsBlock`. `LinkGlyph` has a second one there —
 * the theatre bar's „Dostosuj materiały" — and that one is not a brand at all, which is exactly why
 * the neutral drawing takes `currentColor` while the three logos do not.
 *
 * ── The one place a literal colour is right ─────────────────────────────────────────────────
 * Every other surface in this app takes its ink from a token, and stylelint fails the build on a
 * literal. A LOGO is the exception and it is a narrow one: YouTube's red is YouTube's red in both
 * themes, and recolouring a trademark to fit a palette is both wrong and confusing. The fills are
 * inline SVG attributes rather than CSS for exactly that reason — they are part of the drawing, not
 * a styling decision the theme may revisit — which is also why stylelint never sees them.
 *
 * The plate BEHIND the mark is the caller's, and it is a token. Both themes need one: a
 * multi-colour logo on a near-black ground loses its dark strokes, and the canon puts every brand
 * mark on a light plate for that reason.
 *
 * The neutral glyph is the odd one out and takes `currentColor`, because it is not a logo — it is
 * the app's own drawing of "a link", and it belongs to the ink around it.
 */

import type { LinkBrandKey } from '../model/exerciseLinks';

interface MarkProps {
  readonly className?: string;
}

function YouTubeMark({ className }: MarkProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="#FF0000"
        d="M23 7.5a3 3 0 0 0-2.1-2.1C19 4.9 12 4.9 12 4.9s-7 0-8.9.5A3 3 0 0 0 1 7.5C.5 9.4.5 12 .5 12s0 2.6.5 4.5a3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-4.5.5-4.5s0-2.6-.5-4.5z"
      />
      <path fill="#ffffff" d="M9.75 15.5v-7l6 3.5z" />
    </svg>
  );
}

function DriveMark({ className }: MarkProps) {
  return (
    <svg className={className} viewBox="0 0 87.3 78" aria-hidden focusable="false">
      <path fill="#0066da" d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" />
      <path fill="#00ac47" d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" />
      <path fill="#ea4335" d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" />
      <path fill="#00832d" d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" />
      <path fill="#2684fc" d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" />
      <path fill="#ffba00" d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" />
    </svg>
  );
}

function InstagramMark({ className }: MarkProps) {
  // The gradient id is a module constant rather than a `useId`: this mark is drawn at most three
  // times on a screen and every copy wants the same stops, so duplicate definitions of one id
  // resolve to the first and draw correctly. A per-instance id would be three identical gradients.
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
      <defs>
        <radialGradient id="mb-link-instagram" cx="30%" cy="107%" r="150%">
          <stop offset="0" stopColor="#fdf497" />
          <stop offset=".45" stopColor="#fd5949" />
          <stop offset=".6" stopColor="#d6249f" />
          <stop offset="1" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect x="1.5" y="1.5" width="21" height="21" rx="6" fill="url(#mb-link-instagram)" />
      <rect
        x="6.2"
        y="6.2"
        width="11.6"
        height="11.6"
        rx="4"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.7"
      />
      <circle cx="17.6" cy="6.5" r="1.1" fill="#ffffff" />
    </svg>
  );
}

/** The app's own drawing of "a link". Takes `currentColor`, because it is not a logo. */
export function LinkGlyph({ className }: MarkProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      aria-hidden
      focusable="false"
    >
      <path d="M6.5 9.5a3 3 0 0 0 4.2 0l2-2a3 3 0 0 0-4.2-4.2l-1 1" />
      <path d="M9.5 6.5a3 3 0 0 0-4.2 0l-2 2a3 3 0 0 0 4.2 4.2l1-1" />
    </svg>
  );
}

/**
 * The mark for one material, or the neutral glyph when its host is not one of the three.
 *
 * `brand` is `brandKeyOf`'s answer and never a host: deciding which mark to draw is the entity's
 * rule, and a component that re-read the host would be a second copy of it.
 */
export function LinkBrandMark({
  brand,
  className,
}: MarkProps & { readonly brand: LinkBrandKey | null }) {
  switch (brand) {
    case 'youtube':
      return <YouTubeMark className={className} />;
    case 'drive':
      return <DriveMark className={className} />;
    case 'instagram':
      return <InstagramMark className={className} />;
    case null:
      return <LinkGlyph className={className} />;
  }
}
